import { useState } from 'react';
import { useDemo2Ctx } from '../Demo2Context';
import { WORKERS, calcCentroid, calcPolygonAreaM2, MONITOR_LABEL, type LatLng, type ZoneCategory, type MonitorType } from '../demo2Data';

const MONITOR_OPTIONS: { key: MonitorType; ico: string; tag: string; desc: string }[] = [
    { key: 'sensorless', ico: 'ri-brain-line', tag: '초기도입·좁은면적 추천', desc: 'AI 알고리즘 + 기상데이터 + 위성/드론 영상 기반 예측. 센서 설치 없이 저비용·고효율로 시작' },
    { key: 'sensor', ico: 'ri-radar-line', tag: '넓은면적·고정밀 추천', desc: '토양센서(라이시미터 등) 실측값 + AIS 알고리즘 결합. 설치 비용은 있으나 정확도 높음' },
];

/* 기존 /demo(panels/SiteRegForm.tsx)의 "폴리곤 그려서 신규 지점 등록" 흐름을
 * demo2 관리구역 모델(Zone)에 맞춰 이식한 등록 폼.
 * 기획안 3장 "센서 전략" — 센서리스 vs 센서 기반을 등록 시점에 선택하도록 반영. */
export function Demo2ZoneRegForm({ polygon }: { polygon: LatLng[] }) {
    const { addZone, toast, closePanel } = useDemo2Ctx();
    const areaM2 = Math.round(calcPolygonAreaM2(polygon));
    const centroid = calcCentroid(polygon);

    const [name, setName] = useState('');
    const [category, setCategory] = useState<ZoneCategory>('공원');
    const [monitorType, setMonitorType] = useState<MonitorType>('sensorless');
    const [mgr, setMgr] = useState(WORKERS[0].name);
    const [treeName, setTreeName] = useState('');
    const [treeCount, setTreeCount] = useState('');
    const [soilType, setSoilType] = useState('양토');
    const [ph, setPh] = useState('6.5');
    const [drainage, setDrainage] = useState<'양호' | '보통' | '불량'>('양호');

    const submit = () => {
        if (!name.trim()) { toast('관리구역명을 입력해 주세요', 'error'); return; }
        const id = addZone({
            name: name.trim(), category, monitorType, mgr, treeName: treeName.trim(),
            treeCount: Number(treeCount) || 0, soilType, ph: Number(ph) || 6.5, drainage, polygon,
        });
        toast(`'${name}' 관리구역이 ${MONITOR_LABEL[monitorType]} 방식으로 등록되었습니다 (${id}) ✓`, 'success');
        closePanel();
    };

    return (
        <>
            <div className="rpnl-body">
                <div style={{
                    background: 'var(--primary-light)', border: '1.5px solid var(--primary-mid)',
                    borderRadius: 10, padding: 14, marginBottom: 16,
                }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary-dark)', marginBottom: 6 }}>구역 경계 설정 완료</div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 11.5, color: 'var(--text2)' }}><span style={{ color: 'var(--text3)' }}>꼭지점</span>&nbsp;<strong>{polygon.length}개</strong></div>
                        <div style={{ fontSize: 11.5, color: 'var(--text2)' }}><span style={{ color: 'var(--text3)' }}>면적</span>&nbsp;<strong>{areaM2.toLocaleString()}㎡</strong> <span style={{ color: 'var(--text3)' }}>({Math.round(areaM2 / 3.3058).toLocaleString()}평)</span></div>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text3)' }}>중심좌표 {centroid.lat.toFixed(6)}, {centroid.lng.toFixed(6)}</div>
                </div>

                <div className="form-group" style={{ marginBottom: 16 }}>
                    <label className="form-label">모니터링 방식 <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6 }}>
                        {MONITOR_OPTIONS.map(o => {
                            const active = monitorType === o.key;
                            return (
                                <div
                                    key={o.key}
                                    onClick={() => setMonitorType(o.key)}
                                    style={{
                                        border: `1.5px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                                        background: active ? 'var(--primary-light)' : 'var(--surface)',
                                        borderRadius: 12, padding: '12px 13px', cursor: 'pointer', transition: 'var(--transition)',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                        <i className={o.ico} style={{ fontSize: 18 }} />
                                        <span style={{ fontSize: 12.5, fontWeight: 800, color: active ? 'var(--primary-dark)' : 'var(--text)' }}>{MONITOR_LABEL[o.key]}</span>
                                    </div>
                                    <div style={{ fontSize: 9.5, fontWeight: 700, color: active ? 'var(--primary-dark)' : 'var(--text3)', marginBottom: 5 }}>{o.tag}</div>
                                    <div style={{ fontSize: 10.5, color: 'var(--text3)', lineHeight: 1.6 }}>{o.desc}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="form-group" style={{ marginBottom: 14 }}>
                    <label className="form-label">관리구역명 <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <input className="form-input" placeholder="예: 판교 백현동 근린공원" value={name} onChange={e => setName(e.target.value)} />
                </div>
                <div className="form-row" style={{ marginBottom: 14 }}>
                    <div className="form-group">
                        <label className="form-label">구분</label>
                        <select className="form-select" value={category} onChange={e => setCategory(e.target.value as ZoneCategory)}>
                            <option value="공원">공원</option><option value="가로수">가로수</option><option value="조경수">조경수</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">담당자</label>
                        <select className="form-select" value={mgr} onChange={e => setMgr(e.target.value)}>
                            {WORKERS.map(w => <option key={w.name}>{w.name}</option>)}
                        </select>
                    </div>
                </div>
                <div className="form-row" style={{ marginBottom: 14 }}>
                    <div className="form-group"><label className="form-label">주요 수종</label><input className="form-input" placeholder="예: 느티나무·왕벚나무" value={treeName} onChange={e => setTreeName(e.target.value)} /></div>
                    <div className="form-group"><label className="form-label">식재 수량</label><input type="number" className="form-input" placeholder="0" value={treeCount} onChange={e => setTreeCount(e.target.value)} /></div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">토양 유형</label>
                        <select className="form-select" value={soilType} onChange={e => setSoilType(e.target.value)}>
                            <option>양토</option><option>사질토</option><option>점토</option><option>혼합</option>
                        </select>
                    </div>
                    <div className="form-group"><label className="form-label">토양 pH</label><input type="number" step="0.1" className="form-input" value={ph} onChange={e => setPh(e.target.value)} /></div>
                </div>
                <div className="form-group" style={{ marginTop: 14 }}>
                    <label className="form-label">배수성</label>
                    <select className="form-select" value={drainage} onChange={e => setDrainage(e.target.value as '양호' | '보통' | '불량')}>
                        <option>양호</option><option>보통</option><option>불량</option>
                    </select>
                </div>
            </div>
            <div className="rpnl-ft">
                <button className="btn btn-ghost" onClick={closePanel}>취소</button>
                <button className="btn btn-primary" onClick={submit}>관리구역 등록</button>
            </div>
        </>
    );
}
